import api from '../utils/axiosConfig';
import { Alert, CreateAlertRequest } from '../types';

export class AlertsService {
  /**
   * Get all alerts
   * Calls GET /alerts
   */
  static async getAlerts(): Promise<Alert[]> {
    try {
      const response = await api.get('/alerts', { params: { per_page: 100 } });
      const alerts = response.data.alerts || [];
      return alerts;
    } catch (error: any) {
      console.error('Error fetching alerts:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch alerts');
    }
  }

  /**
   * Get a single alert by ID
   * Calls GET /alerts/<alert_id>
   */
  static async getAlertById(alertId: string): Promise<Alert> {
    try {
      const response = await api.get(`/alerts/${alertId}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching alert:', error);
      if (error.response?.status === 404) {
        throw new Error('Alert not found');
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch alert');
    }
  }

  /**
   * Create a new alert
   * Calls POST /alerts
   */
  static async createAlert(alertData: CreateAlertRequest): Promise<Alert> {
    try {
      const response = await api.post('/alerts', alertData);
      return response.data;
    } catch (error: any) {
      console.error('Error creating alert:', error);
      throw new Error(error.response?.data?.message || 'Failed to create alert');
    }
  }

  /**
   * Update an existing alert
   * Calls PUT /alerts/<alert_id>
   */
  static async updateAlert(alertId: string, alertData: Partial<CreateAlertRequest>): Promise<Alert> {
    try {
      const response = await api.put(`/alerts/${alertId}`, alertData);
      return response.data;
    } catch (error: any) {
      console.error('Error updating alert:', error);
      if (error.response?.status === 404) {
        throw new Error('Alert not found');
      }
      throw new Error(error.response?.data?.message || 'Failed to update alert');
    }
  }

  /**
   * Delete an alert
   * Calls DELETE /alerts/<alert_id>
   */
  static async deleteAlert(alertId: string): Promise<void> {
    try {
      await api.delete(`/alerts/${alertId}`);
    } catch (error: any) {
      console.error('Error deleting alert:', error);
      if (error.response?.status === 404) {
        throw new Error('Alert not found');
      }
      throw new Error(error.response?.data?.message || 'Failed to delete alert');
    }
  }

  /**
   * Get alerts by client ID
   * Calls GET /alerts?client_id=<client_id>
   */
  static async getAlertsByClient(clientId: string): Promise<Alert[]> {
    try {
      const response = await api.get(`/alerts`, {
        params: { client_id: clientId }
      });
      // Backend returns { alerts: [...], pagination: {...} }
      return response.data.alerts || [];
    } catch (error: any) {
      console.error('Error fetching alerts by client:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch alerts for client');
    }
  }
}
